/* eslint-disable indent */
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { twMerge as mergeClassNames } from 'tailwind-merge'
import PropTypes from 'prop-types'
import _ from 'lodash'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { SparklesIcon } from '@heroicons/react/24/solid'
import { Controller, useForm } from 'react-hook-form'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'

// Images
import { ChevronDownIcon, ChevronUpIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid'

// Components
import { Button } from '../../components/Button'
import { EditIcon } from '../../components/EditIcon'
import { FileUploader } from '../../components/FileUploader'
import { Modal } from '../../components/Modal'
import { PlusIcon } from '../../components/PlusIcon'
import { Select } from '../../components/Select'
import { Toggle } from '../../components/Toggle'

// Helpers
import { CUSTOM_BLOCKS, PRESET_BLOCKS } from './helpers'

// Store
import { NavigationStoreContext } from '../../stores/NavigationStore'

// Utils & Services
import { MAX_BADGE_LAYERS, NON_SYNCED_FIELDS } from '../../utils/constants'
import { toast } from '../../utils/helpers'
import { addBadgeImage } from '../../services/badges.service'

/**
 *
 * BadgeContentEditor
 *
 */
/**
 * Returns a human-readable label for a badge element.
 */
const getElementLabel = (element) => {
  if (!element) return 'Unknown'
  const { className, attrs } = element

  if (attrs?.id === 'base') return 'Background'

  if (className === 'Text') {
    if (attrs?.accessor === 'fullName') return 'Full Name'
    if (attrs?.accessor === 'title') return 'Job Title'
    if (attrs?.accessor === 'companyName') return 'Company'
    if (attrs?.accessor === 'presetLocation') return 'Location'
    if (attrs?.accessor) return `Merge: ${attrs.accessor}`
    const text = attrs?.text || ''
    if (text.length > 20) return `Text: ${text.substring(0, 20)}...`
    return `Text: ${text || '(empty)'}`
  }

  if (className === 'Image') {
    if (attrs?.customQr) return 'Custom QR Code'
    if (attrs?.qrImage) return 'App QR Code'
    if (attrs?.isBackground) return 'Background Image'
    return 'Image'
  }

  if (className === 'Rect') return 'Rectangle'
  if (className === 'Group') return `Group (${element.children?.length || 0})`
  if (className === 'List') return `List (${attrs?.lines?.length || 0})`

  return className || 'Element'
}

const BadgeContentEditor = ({
  activeElement,
  addElementToPanel,
  handleChangeStockSize,
  handlePanelUpdates,
  enableMirrorContent,
  layers: panelLayers,
  mergeFields,
  moveLayer,
  numOfPanels,
  panelOptions,
  readOnly,
  selectedPanel,
  setActiveElement,
  setSelectedPanel,
  showGrid,
  setShowGrid,
  stockOptions,
  stockSize,
  toggleLayerSettings,
  updateLayerVisibility,
}) => {
  // Context
  const { event } = useContext(NavigationStoreContext)

  // State
  const [panelViewOpen, setPanelViewOpen] = useState(true)
  const [layersOpen, setLayersOpen] = useState(true)
  const [blocksOpen, setBlocksOpen] = useState(true)
  const [elementsOpen, setElementsOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('Preset')
  const [layers, setLayers] = useState(panelLayers)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [customMergeField, setCustomMergeField] = useState(null)
  const [showFileUploader, setShowFileUploader] = useState(false)
  const [showChangePanelModal, setShowChangePanelModal] = useState(false)
  const [updatedStockSize, setUpdatedStockSize] = useState(stockSize)

  const handleErrors = (message) => toast(message, 'error')

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      mergeField: null,
    },
  })

  useEffect(() => {
    setLayers(panelLayers)
  }, [panelLayers])

  const mergeFieldOptions = useMemo(() => {
    let fields = _.map(
      _.filter(NON_SYNCED_FIELDS, (f) => f !== 'categoryName'),
      (f) => ({ id: f, label: f }),
    )

    // Manually add category since we want to use `categoryName` as the accessor
    fields.unshift({ id: 'categoryName', label: 'Category' })

    // Manually add Cvent-only fields after Category
    if (event?.registrationType === 'Cvent') {
      fields.splice(1, 0, { id: 'nickname', label: 'Nickname' })
    }

    if (event?.admissionItems) {
      fields.unshift({ id: 'admissionItem', label: 'Admission Item' })
    }

    if (mergeFields) {
      const syncedFields = _.map(_.keys(mergeFields), (k) => ({
        id: k,
        label: mergeFields[k].text,
      }))
      fields = _.concat(fields, syncedFields)
    }

    return fields
  }, [mergeFields, event?.admissionItems, event?.registrationType])

  const categories = useMemo(
    () =>
      _.map(_.keys(event?.attendeeCategories), (k) => ({
        id: k,
        label: event?.attendeeCategories[k],
      })),
    [event],
  )

  /**
   * Handles the drag and drop reordering of the layers.
   *
   * The draggable list renders `_.drop(layers, 1)` — the base layer is drawn
   * separately and is not draggable — so drag indices are relative to the
   * non-base layers. `layers` (and the panel's `children` that `moveLayer`
   * splices) still include base at index 0, so every drag index has to be
   * shifted by one before it is used against those arrays.
   * @param {object} result
   */
  const onDragEnd = async (result) => {
    // Dropped outside the list, or didn't actually move
    if (!result.destination || result.destination.index === result.source.index) {
      return
    }

    // Shift out of drag-index space (non-base) and into panel-children space.
    // Both are >= 1, so base at index 0 can never be displaced.
    const source = result.source.index + 1
    const destination = result.destination.index + 1

    // Reorder the layers within the badge
    moveLayer(source, destination)

    // Update local state
    const updatedLayers = [...layers]
    const [removed] = updatedLayers.splice(source, 1)
    updatedLayers.splice(destination, 0, removed)

    setLayers(updatedLayers)
  }

  /**
   * Creates a temporary image element to get the image dimensions, then calculates and returns the aspect ratio.
   * @param {string} url The URL of the image
   * @returns {Promise} The aspect ratio of the image
   */
  const getImageDimensions = async (url) => {
    const img = new Image()
    img.src = url
    return new Promise((resolve, reject) => {
      img.onload = () => {
        resolve({ aspectRatio: img.width / img.height })
      }
      img.onerror = (err) => reject(err)
    })
  }

  /**
   * Renders a list of block elements.
   * @param {Array} blocks
   */
  const renderBlocks = (blocks) =>
    blocks.map((block) => (
      <>
        <button
          key={block.name}
          className="mb-3 flex w-full cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-white bg-white/25 px-2.5 py-4 font-medium"
          onClick={() => {
            if (block.className === 'Image' && !block.element?.attrs?.qrImage) {
              setShowFileUploader(true)
            } else if (block.name === 'Merge Field') {
              setCustomMergeField(block)
            } else {
              addElementToPanel(block, 0)
            }
          }}
          type="button"
        >
          <span className="flex w-[20px] justify-center">{block.icon}</span>
          <span className="text-sm">{block.name}</span>
        </button>
        {block.name === 'Image' && showFileUploader && (
          <FileUploader
            whiteBackground
            acceptedFileTypes={['image/*']}
            allowResize
            allowRevert
            handleUploadToServer={async (file) => {
              const badgeImage = await addBadgeImage(
                event.id,
                { url: file.url },
                handleErrors,
                () => {},
                () => {},
              )

              if (badgeImage) {
                const { aspectRatio } = await getImageDimensions(badgeImage.signedUrl)
                const scaledHeight = 300 / aspectRatio

                const imageElement = {
                  attrs: {
                    src: badgeImage.signedUrl,
                    badgeImagePath: `/events/${event.id}/badge-images/${badgeImage.id}`,
                    height: scaledHeight,
                    width: 300,
                  },
                  className: 'Image',
                }

                const updatedBlock = { ...block }
                updatedBlock.element = imageElement

                setShowFileUploader(false)
                addElementToPanel(updatedBlock, 0)
              }
            }}
            id="badgeImageUrl"
            imageCropAspectRatio="1:1"
            maxFileSize="750KB"
            type="gcp"
          />
        )}
      </>
    ))

  /**
   * Renders a collapsible section with specified `label` and `content`.
   * @param {string} label
   * @param {func} onClick
   * @param {boolean} active
   * @param {element} content
   */
  const renderCollapsible = (label, onClick, active, content) => (
    <div>
      <button
        type="button"
        onClick={onClick}
        className="my-5 flex w-full cursor-pointer items-center justify-between font-bold uppercase"
      >
        <span className="text-sm">{label}</span>
        {active ? (
          <ChevronUpIcon className="h-5 fill-white" />
        ) : (
          <ChevronDownIcon className="h-5 fill-white" />
        )}
      </button>

      {active && <div>{content}</div>}
    </div>
  )

  return (
    <div className="h-full w-[30%] overflow-y-auto rounded-xl bg-darkBlue px-5 pb-2 text-white">
      {renderCollapsible(
        'Panel view',
        () => setPanelViewOpen(!panelViewOpen),
        panelViewOpen,
        <div className="space-y-2 pl-2">
          <Button
            label="Change Panel Size"
            onClick={() => setShowChangePanelModal(true)}
            fullWidth
          />

          <div className="space-y-2">
            <span className="text-sm font-semibold">Select Panel</span>
            <Select
              className="border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
              data-testid="panel"
              fullWidth
              id="panel"
              nunito
              onChange={(o) => {
                if (o.id === -1) {
                  // Add a new panel
                  handlePanelUpdates('add')
                } else {
                  setSelectedPanel(o.id)
                }
              }}
              options={panelOptions}
              style={{ flex: true, width: '100%' }}
              value={_.find(panelOptions, { id: selectedPanel })}
            />

            {numOfPanels > 1 && (
              <Toggle
                inter
                label={<span className="text-sm text-white">Same Content Front & Back</span>}
                onChange={() => {
                  if (readOnly) return
                  if (enableMirrorContent) {
                    handlePanelUpdates('mirror')
                  } else {
                    setShowConfirmModal(true)
                  }
                }}
                checked={enableMirrorContent}
                disabled={readOnly}
              />
            )}
          </div>

          <Toggle
            inter
            label={<span className="text-sm text-white">Show Grid Lines</span>}
            onChange={() => {
              setShowGrid(!showGrid)
            }}
            checked={showGrid}
          />
        </div>,
      )}

      {layers &&
        renderCollapsible(
          'Layers',
          () => setLayersOpen(!layersOpen),
          layersOpen,
          <div className="flex flex-col pl-2">
            <div
              key="base"
              className="pointer-events-none flex w-full items-center justify-between"
              role="button"
              title="Toggle layer settings"
            >
              <div className="flex flex-row gap-2">
                <span className="text-sm">Base</span>
              </div>
            </div>

            {/* Scrolls once the layer list outgrows the sidebar */}
            <div className="max-h-[30vh] overflow-y-auto">
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                  {(provided) => (
                    <div
                      className="flex flex-col"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {_.drop(layers, 1).map((layer, i) => {
                        let layerName = layer.attrs.id === 'base' ? 'Base' : layer.attrs.name
                        if (layer.attrs.id !== 'base' && !layer.attrs.name)
                          layerName = `Layer ${layer.attrs.index ?? i + 1}`

                        const match = _.find(categories, (c) =>
                          layer.attrs.visibility.includes(c.id),
                        )
                        const category = match ? match.label : ''

                        return (
                          <Draggable
                            key={layer.attrs.id}
                            draggableId={layer.attrs.id}
                            index={i}
                            isDragDisabled={readOnly}
                          >
                            {(p, s) => (
                              /* eslint-disable-next-line jsx-a11y/interactive-supports-focus, jsx-a11y/click-events-have-key-events */
                              <div
                                className="flex h-7 w-full items-center justify-between py-1.5"
                                onClick={() => !readOnly && toggleLayerSettings(layer)}
                                role="button"
                                title={
                                  readOnly
                                    ? 'View only'
                                    : 'Drag to reorder, click for layer settings'
                                }
                                ref={p.innerRef}
                                style={{
                                  ...p.draggableProps.style,
                                  cursor: readOnly ? 'default' : 'grab',
                                }}
                                {...p.draggableProps}
                                {...p.dragHandleProps}
                              >
                                <div className="flex flex-row gap-2">
                                  <span
                                    className={mergeClassNames(
                                      'text-sm',
                                      s.isDragging && 'text-purple',
                                    )}
                                  >
                                    {layerName}
                                  </span>

                                  {layer.attrs.visibility &&
                                    layer.attrs.visibility !== 'always' && (
                                      <span className="font-white rounded-full px-1.5 py-0.5 text-xs">
                                        {category}
                                      </span>
                                    )}
                                </div>

                                {/* Display visibility control */}
                                <button
                                  className="px-1 py-0.5"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (readOnly) return

                                    updateLayerVisibility({
                                      ...layer,
                                      attrs: { ...layer.attrs, visible: !layer.attrs.visible },
                                    })
                                  }}
                                  title={readOnly ? 'View only' : 'Toggle layer visibility'}
                                  type="button"
                                  disabled={readOnly}
                                  style={{ cursor: readOnly ? 'default' : 'pointer' }}
                                >
                                  {layer.attrs.visible ? (
                                    <EyeIcon className="h-5 w-5" />
                                  ) : (
                                    <EyeSlashIcon className="h-5 w-5" />
                                  )}
                                </button>
                              </div>
                            )}
                          </Draggable>
                        )
                      })}

                      {/* Reserves space for the row being dragged. Belongs to the
                          droppable, not to each draggable. */}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            {!readOnly && layers.length <= MAX_BADGE_LAYERS && (
              <button
                type="button"
                onClick={() => addElementToPanel({ className: 'Layer' })}
                className="mt-1 flex items-center fill-white hover:fill-purple hover:text-purple"
              >
                <PlusIcon className="h-5 fill-inherit" />
                <span className="text-sm">Add Layer</span>
              </button>
            )}
          </div>,
        )}

      {layers &&
        renderCollapsible(
          'Elements',
          () => setElementsOpen(!elementsOpen),
          elementsOpen,
          <div className="flex max-h-[20vh] flex-col gap-0.5 overflow-y-auto pl-2">
            {(() => {
              const allElements = []

              _.forEach(layers, (layer, layerIndex) => {
                const layerName =
                  layer.attrs.id === 'base'
                    ? 'Base'
                    : layer.attrs.name || `Layer ${layer.attrs.index || layerIndex}`

                _.forEach(layer.children, (element, elementIndex) => {
                  if (element.attrs?.id === 'base') return

                  const isSelected = activeElement?.attrs?.id === element.attrs?.id

                  allElements.push(
                    <button
                      key={element.attrs?.id || `${layerIndex}-${elementIndex}`}
                      type="button"
                      disabled={readOnly}
                      className={mergeClassNames(
                        'flex w-full items-center justify-between rounded px-2 py-1 text-left text-sm',
                        readOnly && 'cursor-default opacity-60',
                        isSelected ? 'bg-purple/30 text-purple' : !readOnly && 'hover:bg-white/10',
                      )}
                      onClick={() => !readOnly && setActiveElement(element)}
                      title={
                        readOnly
                          ? getElementLabel(element)
                          : `${getElementLabel(element)} (${layerName})`
                      }
                    >
                      <span className="truncate">{getElementLabel(element)}</span>
                      <span className="ml-2 shrink-0 text-xs opacity-50">{layerName}</span>
                    </button>,
                  )

                  // Handle group children
                  if (element.className === 'Group' && element.children) {
                    _.forEach(element.children, (groupChild, groupIndex) => {
                      const isGroupChildSelected =
                        activeElement?.attrs?.id === groupChild.attrs?.id

                      allElements.push(
                        <button
                          key={
                            groupChild.attrs?.id || `${layerIndex}-${elementIndex}-${groupIndex}`
                          }
                          type="button"
                          disabled={readOnly}
                          className={mergeClassNames(
                            'flex w-full items-center justify-between rounded py-1 pl-5 pr-2 text-left text-sm',
                            readOnly && 'cursor-default opacity-60',
                            isGroupChildSelected
                              ? 'bg-purple/30 text-purple'
                              : !readOnly && 'hover:bg-white/10',
                          )}
                          onClick={() => !readOnly && setActiveElement(groupChild)}
                          title={
                            readOnly
                              ? getElementLabel(groupChild)
                              : `${getElementLabel(groupChild)} (${layerName})`
                          }
                        >
                          <span className="truncate">{getElementLabel(groupChild)}</span>
                          <span className="ml-2 shrink-0 text-xs opacity-50">{layerName}</span>
                        </button>,
                      )
                    })
                  }
                })
              })

              if (allElements.length === 0) {
                return (
                  <span className="text-xs italic opacity-50">No elements on this panel yet.</span>
                )
              }

              return allElements
            })()}
          </div>,
        )}

      {!readOnly &&
        renderCollapsible(
          'Blocks',
          () => setBlocksOpen(!blocksOpen),
          blocksOpen,
          <div>
            <div className="mb-3">
              <button
                type="button"
                className={mergeClassNames(
                  'mr-4 border-b-2 border-transparent hover:border-purple hover:text-purple',
                  activeTab === 'Preset' && 'border-purple text-purple',
                )}
                onClick={() => setActiveTab('Preset')}
              >
                <span className="text-sm">Preset</span>
              </button>

              <button
                type="button"
                className={mergeClassNames(
                  'mr-4 border-b-2 border-transparent hover:border-purple hover:text-purple',
                  activeTab === 'Custom' && 'border-purple text-purple ',
                )}
                onClick={() => setActiveTab('Custom')}
              >
                <span className="text-sm">Custom</span>
              </button>
            </div>

            {activeTab === 'Preset' && <div>{renderBlocks(PRESET_BLOCKS)}</div>}
            {activeTab === 'Custom' && <div>{renderBlocks(CUSTOM_BLOCKS)}</div>}
          </div>,
        )}

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => setShowConfirmModal(false),
          },
          {
            type: 'submit',
            label: 'Confirm',
            onClick: () => {
              handlePanelUpdates('mirror')
              setShowConfirmModal(false)
            },
            background: 'bg-orange',
          },
        ]}
        icon={<ExclamationTriangleIcon className="h-8 text-white" />}
        iconBackground="bg-orange"
        content={
          <div className="mt-3 flex text-center sm:mt-5">
            <span className="text-sm">
              This will duplicate the content from the front panel to the back, and the back panel
              will not be accessible.
            </span>
          </div>
        }
        open={showConfirmModal}
        title="Are you sure?"
      />

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => setCustomMergeField(false),
          },
          {
            type: 'submit',
            label: 'Add to Badge',
            onClick: handleSubmit((data) => {
              const updatedElement = {
                ...customMergeField,
                element: {
                  ...customMergeField.element,
                  attrs: {
                    ...customMergeField.element.attrs,
                    text: `{{ ${data.mergeField.label} }}`,
                    accessor: data.mergeField.id,
                  },
                },
              }

              addElementToPanel(updatedElement, 0)
              reset()
              setCustomMergeField(null, 0)
            }),
          },
        ]}
        icon={<SparklesIcon className="h-8 fill-white" />}
        content={
          <div className="mt-3 flex text-center sm:mt-5">
            {customMergeField && (
              <Controller
                control={control}
                name="mergeField"
                render={({ field: { onChange, value } }) => (
                  <Select
                    className="rounded-2xl border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
                    data-testid="mergeField"
                    error={errors.mergeField && 'This field is required'}
                    fullWidth
                    id="mergeField"
                    nunito
                    onChange={onChange}
                    options={mergeFieldOptions}
                    label="Select Merge Field"
                    placeholder="Select Merge Field"
                    search
                    style={{ flex: true, width: '100%' }}
                    value={value}
                  />
                )}
                rules={{ required: true }}
              />
            )}
          </div>
        }
        open={!!customMergeField}
        title="Add a Custom Merge Field"
      />

      <Modal
        actions={[
          {
            type: 'cancel',
            label: 'Cancel',
            onClick: () => setShowChangePanelModal(false),
          },
          {
            type: 'submit',
            label: 'Confirm',
            onClick: () => {
              handleChangeStockSize(_.find(stockOptions, { id: updatedStockSize }))
              setShowChangePanelModal(false)
            },
          },
        ]}
        icon={<EditIcon className="h-5 fill-white stroke-white sm:h-6" />}
        content={
          <div className="mt-3 flex flex-col space-y-4 text-center sm:mt-5">
            <span className="text-sm">
              This will affect all panels for your badge. If the size if smaller than the current
              size, elements will be repositioned to fit the new size.
            </span>

            <Select
              className="border-gray-550 py-2.5 pr-4 placeholder:font-normal placeholder:text-gray-600 focus-within:border-purple"
              data-testid="badge-size"
              fullWidth
              id="panel"
              nunito
              onChange={(o) => setUpdatedStockSize(o.id)}
              options={stockOptions}
              style={{ flex: true, width: '100%' }}
              value={_.find(stockOptions, { id: updatedStockSize })}
            />
          </div>
        }
        open={showChangePanelModal}
        title="Change Panel Size"
      />
    </div>
  )
}

BadgeContentEditor.defaultProps = {
  mergeFields: [],
  readOnly: false,
}

BadgeContentEditor.propTypes = {
  activeElement: PropTypes.object,
  addElementToPanel: PropTypes.func.isRequired,
  handleChangeStockSize: PropTypes.func.isRequired,
  handlePanelUpdates: PropTypes.func.isRequired,
  enableMirrorContent: PropTypes.bool.isRequired,
  layers: PropTypes.array.isRequired,
  mergeFields: PropTypes.object,
  moveLayer: PropTypes.func.isRequired,
  numOfPanels: PropTypes.number.isRequired,
  panelOptions: PropTypes.array.isRequired,
  readOnly: PropTypes.bool,
  setActiveElement: PropTypes.func.isRequired,
  showGrid: PropTypes.bool.isRequired,
  selectedPanel: PropTypes.number.isRequired,
  setSelectedPanel: PropTypes.func.isRequired,
  setShowGrid: PropTypes.func.isRequired,
  stockOptions: PropTypes.array.isRequired,
  stockSize: PropTypes.object.isRequired,
  toggleLayerSettings: PropTypes.func.isRequired,
  updateLayerVisibility: PropTypes.func.isRequired,
}

export default BadgeContentEditor
